import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

// ANX-084: тема панели — светлая по умолчанию, ручной тоггл свет/тьма,
// выбор в localStorage. Тёмная тема реализована классом `.dark` на <html>
// (Tailwind darkMode: 'class') + override-карта в index.css.
export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'anexica.theme.v1';

/** Применяет тему к документу: класс `.dark` и color-scheme (нативные контролы). */
export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.toggle('dark', theme === 'dark');
  el.style.colorScheme = theme;
}

/** Читает persisted-тему напрямую из localStorage (для раннего применения
 *  до рендера React — без вспышки светлой темы). */
export function readPersistedTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'light';
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw) {
      const t = (JSON.parse(raw) as { state?: { theme?: unknown } })?.state?.theme;
      if (t === 'dark' || t === 'light') return t;
    }
  } catch {
    /* битый JSON — падаем на светлую */
  }
  return 'light';
}

/** Применяет сохранённую тему как можно раньше (вызывается из main.tsx до рендера). */
export function initTheme(): void {
  applyTheme(readPersistedTheme());
}

interface ThemeState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      setTheme: (theme) => {
        applyTheme(theme);
        set({ theme });
      },
      toggle: () => {
        const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
        applyTheme(next);
        set({ theme: next });
      },
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state) applyTheme(state.theme);
      },
    },
  ),
);
